/**
 * Program 12.1 UX3 — Edit prequal requirement rows in meta_json.
 */
import React, { useState } from 'react';
import { Button, Input, Select, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  type PrequalRequirementRow,
  type PrequalRequirementStatus,
} from './prequalRequirementMatrix';

const STATUS_OPTIONS: { value: PrequalRequirementStatus; label: string }[] = [
  { value: 'COMPLETE', label: 'Complete' },
  { value: 'IN_REVIEW', label: 'In review' },
  { value: 'MISSING', label: 'Missing' },
  { value: 'NOT_APPLICABLE', label: 'N/A' },
];

export const DEFAULT_PREQUAL_REQUIREMENTS: PrequalRequirementRow[] = [
  { id: 'fin-1', section: 'Financial', requirement: 'Audited financial statements', status: 'MISSING', critical: true },
  { id: 'hse-1', section: 'HSE', requirement: 'ISO 45001 certificate', status: 'MISSING', critical: true },
  { id: 'qa-1', section: 'Quality', requirement: 'ISO 9001 certificate', status: 'MISSING' },
  { id: 'ref-1', section: 'References', requirement: 'Similar project references', status: 'MISSING' },
  { id: 'ins-1', section: 'Insurance', requirement: 'Professional indemnity cover', status: 'MISSING' },
];

export type EpcPrequalRequirementEditorProps = {
  rows: PrequalRequirementRow[];
  saving?: boolean;
  onSave: (rows: PrequalRequirementRow[]) => void;
};

export const EpcPrequalRequirementEditor: React.FC<EpcPrequalRequirementEditorProps> = ({
  rows,
  saving,
  onSave,
}) => {
  const [draft, setDraft] = useState<PrequalRequirementRow[]>(rows);

  React.useEffect(() => {
    setDraft(rows);
  }, [rows]);

  const updateRow = (id: string, patch: Partial<PrequalRequirementRow>) => {
    setDraft((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const columns: ColumnsType<PrequalRequirementRow> = [
    { title: 'Section', dataIndex: 'section', width: 110 },
    { title: 'Requirement', dataIndex: 'requirement', ellipsis: true },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (s: PrequalRequirementStatus, r) => (
        <Select
          size="small"
          value={s}
          options={STATUS_OPTIONS}
          onChange={(status) => updateRow(r.id, { status })}
          style={{ width: 120 }}
        />
      ),
    },
    {
      title: 'Evidence',
      dataIndex: 'evidence',
      width: 140,
      render: (v: string | undefined, r) => (
        <Input
          size="small"
          value={v || ''}
          placeholder="File / link"
          onChange={(e) => updateRow(r.id, { evidence: e.target.value })}
        />
      ),
    },
    {
      title: 'Critical',
      dataIndex: 'critical',
      width: 80,
      render: (c: boolean | undefined) => (c ? <Tag color="red">Yes</Tag> : '—'),
    },
  ];

  const effective = draft.length ? draft : DEFAULT_PREQUAL_REQUIREMENTS;

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={effective}
        pagination={false}
        scroll={{ x: 720 }}
      />
      <Space>
        <Button
          type="primary"
          loading={saving}
          onClick={() => onSave(effective)}
        >
          Save requirements
        </Button>
        {rows.length === 0 && (
          <Button onClick={() => setDraft(DEFAULT_PREQUAL_REQUIREMENTS)}>
            Load default template
          </Button>
        )}
      </Space>
    </Space>
  );
};

export default EpcPrequalRequirementEditor;
